/**
 * TrainingLibrary — Excel-driven training data for AI agents.
 *
 * Admin uploads .xlsx / .xls, picks a template (Q&A / KB / Catalog / Generic),
 * attaches the file to one or many bots. Attached files' rows are auto-injected
 * into the bot's public config response as `training_context`.
 *
 * Central library + attach-to-many-bots pattern (option 7b from user).
 * Direct-injection strategy for MVP (option 6c). RAG deferred.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2, Info, Bot,
  X, AlertTriangle,
  FileQuestion, BookOpen, ShoppingBag, Layers,
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { getAuthHeaders } from '../utils/authHeaders';
import LiveTrainingChat from './LiveTrainingChat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = {
  qa:      { icon: FileQuestion, color: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', gradient: 'from-emerald-500/15 to-transparent', labelAr: 'أسئلة وأجوبة',       labelEn: 'Q & A',          requires: 'question, answer',          hintAr: 'عمودان: question + answer',                       hintEn: 'Two columns: question + answer' },
  kb:      { icon: BookOpen,     color: 'text-blue-300',    bg: 'bg-blue-500/15',    border: 'border-blue-500/40',    gradient: 'from-blue-500/15 to-transparent',    labelAr: 'قاعدة معرفية',        labelEn: 'Knowledge Base', requires: 'topic, content',            hintAr: 'عمودان: topic + content',                         hintEn: 'Two columns: topic + content' },
  catalog: { icon: ShoppingBag,  color: 'text-orange-300',  bg: 'bg-orange-500/15',  border: 'border-orange-500/40',  gradient: 'from-orange-500/15 to-transparent',  labelAr: 'كتالوج منتجات/خدمات', labelEn: 'Catalog',        requires: 'name, price (+ optional: description, sku, currency)', hintAr: 'name + price ± description ± sku ± currency', hintEn: 'name + price ± description ± sku ± currency' },
  generic: { icon: Layers,       color: 'text-purple-300',  bg: 'bg-purple-500/15',  border: 'border-purple-500/40',  gradient: 'from-purple-500/15 to-transparent',  labelAr: 'عام / حر',            labelEn: 'Generic',        requires: 'أي أعمدة (any columns)',    hintAr: 'أي أعمدة — تُحقن كما هي',                          hintEn: 'Any columns — injected as-is' },
};

function fmtBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtDate(iso, language) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(language === 'ar' ? 'ar-AE' : 'en-AE', {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Upload Dialog
// ─────────────────────────────────────────────────────────────────────────────
function UploadDialog({ open, onClose, agents, onUploaded, language, preSelectedAgentKey }) {
  const [file, setFile] = useState(null);
  const [template, setTemplate] = useState('qa');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [attachedKeys, setAttachedKeys] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Pre-select the currently focused bot when opened from Live Training
      if (preSelectedAgentKey) setAttachedKeys([preSelectedAgentKey]);
    } else {
      setFile(null);
      setTemplate('qa');
      setName('');
      setDescription('');
      setAttachedKeys([]);
      setError(null);
    }
  }, [open, preSelectedAgentKey]);

  const submit = async () => {
    setError(null);
    if (!file) {
      setError(language === 'ar' ? 'اختر ملف Excel أولاً' : 'Please select an Excel file');
      return;
    }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setError(language === 'ar' ? 'الملف يجب أن يكون .xlsx أو .xls' : 'File must be .xlsx or .xls');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(language === 'ar' ? 'حجم الملف يتجاوز 10 ميجا' : 'File exceeds 10 MB');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('template', template);
    if (name) fd.append('name', name);
    if (description) fd.append('description', description);
    if (attachedKeys.length) fd.append('agent_keys', attachedKeys.join(','));

    setUploading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/training-files/upload`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(language === 'ar'
        ? `تم رفع ${res.data.data.rows_count} صف بنجاح`
        : `Uploaded ${res.data.data.rows_count} rows`);
      onUploaded();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setError(detail);
    } finally {
      setUploading(false);
    }
  };

  const toggleAgent = (k) => {
    setAttachedKeys((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  };

  const tplMeta = TEMPLATES[template];
  const TplIcon = tplMeta.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl bg-[#09090b] border-white/10 text-white p-0 overflow-hidden"
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        data-testid="training-upload-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-white/10 bg-gradient-to-br from-[#d4af37]/10 to-transparent">
          <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#d4af37]" />
            {language === 'ar' ? 'رفع ملف تدريب جديد' : 'Upload Training File'}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA]">
            {language === 'ar'
              ? 'اختر قالب مطابق لبيانات الـ Excel، ثم اربطه ببوت أو أكثر.'
              : 'Pick a template matching your Excel data, then attach to one or more bots.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 max-h-[65vh] overflow-y-auto space-y-5">
          {/* Template selector */}
          <div>
            <Label className="text-xs text-white/70 mb-2 block">
              {language === 'ar' ? 'قالب البيانات' : 'Data Template'}
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(TEMPLATES).map(([k, m]) => {
                const Icon = m.icon;
                const active = template === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setTemplate(k)}
                    className={`flex flex-col items-start text-start p-3 rounded-lg border transition-all ${
                      active
                        ? `bg-gradient-to-br ${m.gradient} ring-2 ${m.border} border-transparent`
                        : 'bg-black/40 border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`training-template-${k}`}
                  >
                    <Icon className={`h-5 w-5 mb-1.5 ${m.color}`} />
                    <div className="text-xs font-bold text-white">
                      {language === 'ar' ? m.labelAr : m.labelEn}
                    </div>
                    <div className="text-[9px] text-white/50 font-mono mt-0.5 line-clamp-2">
                      {m.requires}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className={`mt-3 p-2.5 rounded-md ${tplMeta.bg} ${tplMeta.border} border text-xs flex items-start gap-2 ${tplMeta.color}`}>
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                {language === 'ar' ? 'الأعمدة المطلوبة: ' : 'Required columns: '}
                <code className="font-mono">{tplMeta.requires}</code>
              </span>
            </div>
          </div>

          {/* File picker */}
          <div>
            <Label className="text-xs text-white/70 mb-2 block">
              {language === 'ar' ? 'ملف Excel (.xlsx / .xls) — الحد الأقصى 10MB' : 'Excel File (.xlsx / .xls) — max 10MB'}
            </Label>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="training-file-input"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={`w-full border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                file
                  ? 'border-[#d4af37]/50 bg-[#d4af37]/5'
                  : 'border-white/15 hover:border-[#d4af37]/40 hover:bg-white/[0.02]'
              }`}
              data-testid="training-file-drop"
            >
              {file ? (
                <div className="space-y-1">
                  <FileSpreadsheet className="h-10 w-10 mx-auto text-[#d4af37]" />
                  <div className="text-sm font-semibold text-white">{file.name}</div>
                  <div className="text-[10px] text-white/50 font-mono">
                    {fmtBytes(file.size)} · {file.name.split('.').pop().toUpperCase()}
                  </div>
                </div>
              ) : (
                <>
                  <FileSpreadsheet className="h-10 w-10 mx-auto text-white/40 mb-2" />
                  <div className="text-sm text-white/70">
                    {language === 'ar' ? 'اضغط لاختيار ملف' : 'Click to choose a file'}
                  </div>
                  <div className="text-[10px] text-white/40 mt-1">
                    {language === 'ar' ? '.xlsx أو .xls · حتى 5000 صف' : '.xlsx or .xls · up to 5,000 rows'}
                  </div>
                </>
              )}
            </button>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-white/70">
                {language === 'ar' ? 'الاسم المعروض (اختياري)' : 'Display Name (optional)'}
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={language === 'ar' ? 'أسئلة الدعم — التأشيرات' : 'Visa Support Q&A'}
                className="bg-black/40 border-white/10 text-white mt-1"
                data-testid="training-name-input"
              />
            </div>
            <div>
              <Label className="text-xs text-white/70">
                {language === 'ar' ? 'الوصف' : 'Description'}
              </Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={language === 'ar' ? 'وصف قصير...' : 'Short description...'}
                className="bg-black/40 border-white/10 text-white mt-1"
                data-testid="training-desc-input"
              />
            </div>
          </div>

          {/* Attach to agents */}
          <div>
            <Label className="text-xs text-white/70 mb-2 block flex items-center gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              {language === 'ar'
                ? `ربط ببوتات (اختياري) — ${attachedKeys.length} محدد`
                : `Attach to Bots (optional) — ${attachedKeys.length} selected`}
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto bg-black/40 border border-white/10 rounded-lg p-2">
              {agents.map((a) => {
                const active = attachedKeys.includes(a.agent_key);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggleAgent(a.agent_key)}
                    className={`flex items-center justify-between p-2 rounded-md text-start transition-colors ${
                      active ? 'bg-[#d4af37]/15 border border-[#d4af37]/40' : 'hover:bg-white/5 border border-transparent'
                    }`}
                    data-testid={`training-attach-${a.agent_key}`}
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-white truncate">{language === 'ar' ? a.name_ar : a.name_en}</div>
                      <div className="text-[10px] text-[#d4af37]/80 font-mono truncate">{a.agent_key}</div>
                    </div>
                    {active && <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0" />}
                  </button>
                );
              })}
              {!agents.length && (
                <div className="text-center text-xs text-white/40 col-span-2 py-4">
                  {language === 'ar' ? 'لا توجد بوتات — أنشئ بعض البوتات أولاً في "إدارة الذكاء الاصطناعي"' : 'No bots yet — create some in "AI Management" first'}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-white/10 bg-black/40">
          <Button variant="ghost" onClick={onClose} className="text-white/70" data-testid="training-upload-cancel">
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={submit}
            disabled={uploading || !file}
            className="bg-gradient-to-r from-[#d4af37] to-[#9b7d20] text-black font-bold"
            data-testid="training-upload-submit"
          >
            {uploading
              ? <><Loader2 className="h-4 w-4 animate-spin me-1.5" />{language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}</>
              : <><Upload className="h-4 w-4 me-1.5" />{language === 'ar' ? 'رفع وتحليل' : 'Upload & Parse'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────
export default function TrainingLibrary({ language, providerFilter }) {
  const [files, setFiles] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [targetKey, setTargetKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // iter-237 — bots now live in `ai_agent_registry`. Map providerFilter
      // (google|anthropic) → registry provider_code (GEM|CLA).
      const providerCode = providerFilter === 'anthropic' ? 'CLA'
                         : providerFilter === 'google'    ? 'GEM'
                         : null;
      const registryUrl = providerCode
        ? `${API_URL}/api/admin/ai-registry?provider_code=${providerCode}`
        : `${API_URL}/api/admin/ai-registry`;
      const [filesRes, agentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/training-files`, { headers: getAuthHeaders() }),
        axios.get(registryUrl, { headers: getAuthHeaders() }),
      ]);
      // Normalise registry docs → shape TrainingLibrary + LiveTrainingChat expect
      const rawAgents = (agentsRes.data?.data || [])
        .filter((a) => (a.status || 'active').toLowerCase() !== 'deprecated' && !a.paused)
        .map((a) => ({
          id: a.agent_code,
          agent_key: a.agent_code,              // legacy alias — attached_agent_keys stores this
          agent_code: a.agent_code,
          name_ar: a.display_name_ar || a.name_ar,
          name_en: a.display_name_en || a.name_en,
          provider: a.provider_code === 'CLA' ? 'anthropic' : 'google',
          provider_code: a.provider_code,
          platform_code: a.platform_code,
          model: a.model_used,
          system_prompt_ar: a.system_prompt || '',
          system_prompt_en: a.system_prompt || '',
          description_ar: a.description_ar || '',
          description_en: a.description_en || '',
        }));
      const providerAgentKeys = new Set(rawAgents.map((a) => a.agent_key));
      // Only files attached to at least one bot of this provider (or unattached files)
      const allFiles = filesRes.data?.data || [];
      const scopedFiles = providerFilter
        ? allFiles.filter((f) => {
            const keys = f.attached_agent_keys || [];
            return keys.length === 0 || keys.some((k) => providerAgentKeys.has(k));
          })
        : allFiles;
      setFiles(scopedFiles);
      setAgents(rawAgents);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التحميل' : 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [language, providerFilter]);

  useEffect(() => { load(); }, [load]);

  // Files attached to the currently-selected bot in Live Training
  const attachedFilesForBot = useMemo(() => {
    if (!targetKey) return [];
    return files.filter((f) => (f.attached_agent_keys || []).includes(targetKey));
  }, [files, targetKey]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/admin/training-files/${deleteTarget.id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? `تم حذف ${deleteTarget.name}` : `Deleted ${deleteTarget.name}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
    }
  };

  return (
    <div className="space-y-5" data-testid="training-library">
      <LiveTrainingChat
        language={language}
        agents={agents}
        providerFilter={providerFilter}
        onSaved={load}
        targetKey={targetKey}
        onTargetKeyChange={setTargetKey}
        attachedFiles={attachedFilesForBot}
        onOpenUpload={() => setUploadOpen(true)}
        onDeleteFile={(f) => setDeleteTarget(f)}
      />

      {/* Upload dialog — pre-selects the current bot */}
      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        agents={agents}
        onUploaded={load}
        language={language}
        preSelectedAgentKey={targetKey}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#09090b] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{language === 'ar' ? 'حذف ملف التدريب؟' : 'Delete training file?'}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {language === 'ar'
                ? `سيتم حذف "${deleteTarget?.name}" نهائياً وإلغاء ربطه بكل البوتات.`
                : `"${deleteTarget?.name}" will be deleted permanently and unlinked from all bots.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30"
              data-testid="training-delete-confirm"
            >
              {language === 'ar' ? 'حذف نهائياً' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
